import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DashboardCard, StatsGrid, StatItem, DataList, DataListItem, ActionsMenuBar, ActionItem, StatusActionButton, Timeline, TimelineSegment, TimelineHandle } from '../../../shared/ui';
import { sessionsV2Api, SessionV2, CreateSessionActivityRequest, UpdateSessionActivityRequest, SessionActivityUpdateRequest, UpdateSessionV2Request, SessionActivity } from '../../../api/sessionsV2Api';
import { buildSessionTimelineSegments } from '../../../shared/utils/buildSessionTimelineSegments';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useDashboardCounters } from '../../../shared/contexts/DashboardCountersContext';
import { useOptimizedSignalR, SessionEvent } from '../../../hooks/useOptimizedSignalR';
import { useToast } from '../../../shared/contexts/ToastContext';
import BulkOpenSessionsSheet from './BulkOpenSessionsSheet';
import BottomSheet from '../../../shared/ui/forms/BottomSheet';
import WorkTypeSelector from '../../../shared/ui/forms/WorkTypeSelector';
import HorizontalCategoryTabs from '../../../shared/ui/HorizontalCategoryTabs';
import { typesOfWorkV2Api, TypeOfWorkV2 } from '../../../api/typesOfWorkV2Api';
import { contractsV2Api, ContractV2 } from '../../../api/contractsV2Api';
import { siteExecutorsApi } from '../../../api/siteExecutorsApi';
import config from '../../../config/config';
import '../../../shared/ui/forms/WorkTypeSelector.css';
import '../../siteShifts/ui/CreateSessionV2Modal.css';
import '../../siteShifts/ui/CreateSessionV2Modal.css';
import styles from './ActiveShiftsV2Card.module.css';
import previewStyles from './ActiveShiftsV2CardPreview.module.css';

export interface ActiveShiftV2 {
  id: number;
  userId: number;
  siteExecutorId?: number;
  siteId: number;
  timeStart: string;
  user?: {
    id: number;
    name: string;
  };
  site?: {
    id: number;
    name: string;
  };
  typeOfWork?: {
    id: number;
    name: string;
  };
}

interface ActiveShiftsV2CardProps {
  siteId: number;
}

interface ActivityItem {
  id: number | string;
  typeOfWorkId: number;
  typeOfWorkName?: string;
  contractId?: number;
  contractTitle?: string;
  timeStart: Date;
  rate: number;
  hours: number;
  percentage: number;
  amount: number;
  isNew: boolean;
  isModified: boolean;
  comment?: string;
  isHoursOnly?: boolean;
}

// Мемоизированный компонент для элемента активности (как в MyShiftCardV2)
const ActivityItemComponent = React.memo<{
  item: ActivityItem;
  index: number;
  activities: ActivityItem[];
  sessionEnd: Date;
  formatTime: (date: Date) => string;
  formatDuration: (hours: number) => { hours: number; minutes: number };
  getActivityEndTime: (items: ActivityItem[], index: number, sessionEndTime: Date) => Date;
  updateActivity: (id: number | string, updates: Partial<ActivityItem>) => void;
  handleDeleteActivity: (id: number | string) => void;
  onCommentClick: (item: ActivityItem) => void;
  onActivityTypeClick: (item: ActivityItem) => void;
  isLast: boolean;
}>(({
  item,
  index,
  activities,
  sessionEnd,
  formatTime,
  formatDuration,
  getActivityEndTime,
  updateActivity,
  handleDeleteActivity,
  onCommentClick,
  onActivityTypeClick,
  isLast
}) => {
  const endTime = getActivityEndTime(activities, index, sessionEnd);
  const nextActivity = index < activities.length - 1 ? activities[index + 1] : null;

  return (
    <React.Fragment>
      <div className="activity-item">
        <div className="activity-row activity-row-1">
          <div className="activity-col-time-block">
            <div className="activity-time-block">
              <div className="activity-time-block-duration">
                <span>{formatDuration(item.hours).hours} ч</span>
                {formatDuration(item.hours).minutes > 0 && (
                  <span> {formatDuration(item.hours).minutes} м</span>
                )}
              </div>
              <div className="activity-time-block-percent">
                <span className="percent-value">{item.percentage.toFixed(1)}%</span>
              </div>
            </div>
          </div>
          <div className="activity-vertical-separator"></div>
          <div className="activity-col-type-rate-amount">
            {item.contractId ? (
              <button
                type="button"
                className="activity-contract-display activity-contract-clickable"
                onClick={() => onActivityTypeClick(item)}
                title="Нажмите, чтобы изменить контракт или переключить на вид работ"
              >
                <span className="activity-contract-badge">Сделка</span>
                <span className="activity-contract-title">{item.contractTitle || `Контракт #${item.contractId}`}</span>
                <i className="ri-arrow-right-s-line activity-contract-edit-icon"></i>
              </button>
            ) : (
              <div
                className="activity-worktype-wrapper"
                onClickCapture={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onActivityTypeClick(item);
                }}
              >
                <WorkTypeSelector
                  value={item.typeOfWorkId}
                  onChange={(value) => value && updateActivity(item.id, { typeOfWorkId: value, contractId: undefined, contractTitle: undefined })}
                  placeholder="Выбрать"
                  compact
                />
              </div>
            )}
            <div className="activity-col-rate-amount">
              {item.contractId ? (
                <div className="activity-col-amount">
                  <label>Сумма</label>
                  <span className="amount-value" style={{ fontStyle: 'italic' }}>По контракту</span>
                </div>
              ) : (
                <>
                  <div className="activity-col-rate">
                    <label>Ставка</label>
                    <span style={{ padding: '8px', backgroundColor: '#2a2a2a', color: '#888', borderRadius: '4px', display: 'block' }}>
                      {item.rate > 0 ? `${item.rate.toLocaleString('ru-RU')} ₽/ч` : '—'}
                    </span>
                  </div>
                  <div className="activity-col-amount">
                    <label>Сумма</label>
                    <span className="amount-value">
                      {item.rate > 0 ? `${item.amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽` : '—'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="activity-col-actions" style={{ flexDirection: 'column', gap: '8px' }}>
            <button
              type="button"
              className="btn-comment"
              onClick={() => onCommentClick(item)}
              title={item.comment ? 'Редактировать комментарий' : 'Добавить комментарий'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                padding: 0,
                background: item.comment ? 'rgba(74, 158, 255, 0.15)' : 'transparent',
                color: item.comment ? '#4a9eff' : '#888',
                border: item.comment ? '1.5px solid rgba(74, 158, 255, 0.3)' : '1.5px solid rgba(136, 136, 136, 0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!item.comment) {
                  e.currentTarget.style.background = 'rgba(74, 158, 255, 0.1)';
                  e.currentTarget.style.color = '#4a9eff';
                  e.currentTarget.style.borderColor = 'rgba(74, 158, 255, 0.4)';
                } else {
                  e.currentTarget.style.background = 'rgba(74, 158, 255, 0.25)';
                  e.currentTarget.style.borderColor = '#4a9eff';
                }
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = item.comment ? 'rgba(74, 158, 255, 0.15)' : 'transparent';
                e.currentTarget.style.color = item.comment ? '#4a9eff' : '#888';
                e.currentTarget.style.borderColor = item.comment ? 'rgba(74, 158, 255, 0.3)' : 'rgba(136, 136, 136, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className={item.comment ? "ri-chat-3-fill" : "ri-chat-3-line"}></i>
            </button>
            <button
              type="button"
              className="btn-delete"
              onClick={() => handleDeleteActivity(item.id)}
              title="Удалить"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>
      </div>
      {isLast ? (
        <div className="activity-separator">
          <div className="activity-separator-line"></div>
          <div className="activity-separator-time">
            <span title="Конец смены" className="activity-start-time-label">
              {formatTime(endTime)}
            </span>
          </div>
        </div>
      ) : nextActivity ? (
        <div className="activity-separator">
          <div className="activity-separator-line"></div>
          <div className="activity-separator-time">
            <input
              type="time"
              value={`${String(nextActivity.timeStart.getHours()).padStart(2, '0')}:${String(nextActivity.timeStart.getMinutes()).padStart(2, '0')}`}
              onChange={(e) => {
                const [h, m] = e.target.value.split(':');
                const newTime = new Date(nextActivity.timeStart);
                newTime.setHours(parseInt(h), parseInt(m));
                updateActivity(nextActivity.id, { timeStart: newTime });
              }}
              className="activity-time-picker-input"
              id={`time-input-${nextActivity.id}`}
            />
          </div>
        </div>
      ) : null}
    </React.Fragment>
  );
}, (prevProps, nextProps) => {
  // Функция сравнения как в MyShiftCardV2
  if (prevProps.item.id !== nextProps.item.id) return false;
  if (prevProps.isLast !== nextProps.isLast) return false;
  if (prevProps.index !== nextProps.index) return false;
  
  const prevHash = `${prevProps.item.timeStart.getTime()}-${prevProps.item.typeOfWorkId}-${prevProps.item.rate}-${prevProps.item.hours}`;
  const nextHash = `${nextProps.item.timeStart.getTime()}-${nextProps.item.typeOfWorkId}-${nextProps.item.rate}-${nextProps.item.hours}`;
  
  return prevHash === nextHash && prevProps.activities.length === nextProps.activities.length;
});

ActivityItemComponent.displayName = 'ActivityItemComponent';

const AVATAR_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#84cc16', '#22c55e', '#10b981', '#06b6d4',
  '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#d946ef', '#ec4899', '#f43f5e',
];

function getAvatarColor(userId: number): string {
  const index = String(userId).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}

function getUserInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getPeopleWordForm(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'человек';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'человека';
  return 'человек';
}

function buildExecutorAvatarMaps(executors: Awaited<ReturnType<typeof siteExecutorsApi.getSiteExecutors>>) {
  const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
  const byUserId = new Map<number, string>();
  const byExecutorId = new Map<number, string>();
  executors.forEach((executor) => {
    if (!executor.avatarUrl?.trim()) return;
    const url = executor.avatarUrl.startsWith('http')
      ? executor.avatarUrl
      : `${apiBaseUrl}${executor.avatarUrl}`;
    byExecutorId.set(executor.id, url);
    if (executor.userId) {
      byUserId.set(executor.userId, url);
    }
  });
  return { byUserId, byExecutorId };
}

const PreviewExecutorAvatar: React.FC<{
  userId: number;
  name: string;
  avatarUrl?: string;
}> = ({ userId, name, avatarUrl }) => {
  const [imgFailed, setImgFailed] = useState(false);

  if (avatarUrl && !imgFailed) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className={previewStyles.avatarImg}
        title={name}
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <span
      className={previewStyles.avatar}
      style={{ backgroundColor: getAvatarColor(userId) }}
      title={name}
    >
      {getUserInitials(name)}
    </span>
  );
};

const ActiveShiftsV2Card: React.FC<ActiveShiftsV2CardProps> = ({ siteId }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false); // Для отслеживания fullscreen модального окна
  const [activeShifts, setActiveShifts] = useState<ActiveShiftV2[]>([]);
  const [avatarByUserId, setAvatarByUserId] = useState<Map<number, string>>(() => new Map());
  const [avatarByExecutorId, setAvatarByExecutorId] = useState<Map<number, string>>(() => new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { user } = useAuth();
  const countersContext = useDashboardCounters();
  const { showSuccess, showError, showWarning } = useToast();
  const [showBulkOpenSheet, setShowBulkOpenSheet] = useState(false);
  const [canCreateSessions, setCanCreateSessions] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [selectedShiftIds, setSelectedShiftIds] = useState<Set<number>>(new Set());
  const [showActionsMenuBar, setShowActionsMenuBar] = useState(false);
  const [closingShifts, setClosingShifts] = useState(false);
  
  // Состояния для массовых операций
  const [bulkAddingCheckpoints, setBulkAddingCheckpoints] = useState(false);
  const [bulkAddingActivities, setBulkAddingActivities] = useState(false);
  const [bulkActivityOverlaps, setBulkActivityOverlaps] = useState<Map<number, any[]>>(new Map());
  const [showBulkConfirmDeleteSheet, setShowBulkConfirmDeleteSheet] = useState(false);
  const [pendingBulkActivityData, setPendingBulkActivityData] = useState<{startTime: Date, endTime: Date} | null>(null);
  
  // После массового открытия ждём ответ GET active (с canEdit), не добавляем смены из SignalR — иначе список обновится без прав и уйдут лишние запросы permissions/edit
  const skipSignalRAddRef = useRef(false);
  // При массовом закрытии не удаляем смены по одному из SignalR — обновим список один раз из GET active
  const skipSignalRRemoveRef = useRef(false);
  // Состояния для раскрытия смен
  const [expandedShiftId, setExpandedShiftId] = useState<number | null>(null);
  const [shiftEditPermissions, setShiftEditPermissions] = useState<Map<number, boolean>>(new Map());
  const [checkingEditPermissions, setCheckingEditPermissions] = useState<Set<number>>(new Set());
  
  // Состояния для работы с активной сменой
  const [selectedSession, setSelectedSession] = useState<SessionV2 | null>(null);
  const [selectedSessionActivities, setSelectedSessionActivities] = useState<SessionActivity[]>([]);
  const [loadingSelectedSessionActivities, setLoadingSelectedSessionActivities] = useState(false);
  
  // Состояния для добавления контрольной точки
  const [isAddingCheckpoint, setIsAddingCheckpoint] = useState(false);
  
  // Состояния для добавления активности
  const [showAddActivitySheet, setShowAddActivitySheet] = useState(false);
  const [activityHours, setActivityHours] = useState<number>(1);
  const [activityMinutes, setActivityMinutes] = useState<number>(0);
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  
  // Состояния для закрытия смены
  const [isEnding, setIsEnding] = useState(false);
  
  // Состояния для bottom sheet заполнения активностей
  const [showActivitiesSheet, setShowActivitiesSheet] = useState(false);
  const [sessionForActivities, setSessionForActivities] = useState<SessionV2 | null>(null);
  const [timeEnd, setTimeEnd] = useState<string>('');
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  
  // Состояния для временной шкалы
  const [draggingHandleIndex, setDraggingHandleIndex] = useState<number | null>(null);
  const [timelineRef, setTimelineRef] = useState<HTMLDivElement | null>(null);
  const [tempTimelinePositions, setTempTimelinePositions] = useState<Map<number, Date> | null>(null);
  
  // Состояния для модального окна выбора видов работ
  const [showWorkTypeSelector, setShowWorkTypeSelector] = useState(false);
  const [selectedWorkTypeIds, setSelectedWorkTypeIds] = useState<number[]>([]);
  const [typesOfWork, setTypesOfWork] = useState<TypeOfWorkV2[]>([]);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
  const [workTypeSearchQuery, setWorkTypeSearchQuery] = useState('');
  const [selectedWorkTypeCategory, setSelectedWorkTypeCategory] = useState<string | null>(null);
  // Вкладки «Ставка» / «Сделка» и контракты
  const [activitySourceTab, setActivitySourceTab] = useState<'ставка' | 'сделка'>('ставка');
  const [selectedContractIds, setSelectedContractIds] = useState<number[]>([]);
  const [contracts, setContracts] = useState<ContractV2[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [contractSearchQuery, setContractSearchQuery] = useState('');
  const [editingActivityId, setEditingActivityId] = useState<number | string | null>(null);
  
  // Состояния для подтверждения удаления пересекающихся активностей
  const [showConfirmDeleteSheet, setShowConfirmDeleteSheet] = useState(false);
  const [activitiesToDelete, setActivitiesToDelete] = useState<any[]>([]);
  const [pendingActivityData, setPendingActivityData] = useState<{startTime: Date, endTime: Date} | null>(null);
  
  // Состояния для комментариев к активностям
  const [showCommentSheet, setShowCommentSheet] = useState(false);
  const [currentActivityForComment, setCurrentActivityForComment] = useState<ActivityItem | null>(null);
  const [activityComment, setActivityComment] = useState<string>('');
  
  // Получаем счетчик из Context (если доступен) или null
  const counter = countersContext?.counters?.activeSessions?.hasAccess 
    ? countersContext.counters.activeSessions.count 
    : null;
  const countersLoading = countersContext?.loading ?? false;
  
  // Количество активностей без вида работ (приходит в том же ответе dashboard-counters)
  const unsetActivitiesCount = countersContext?.counters?.activeSessions?.unsetActivitiesCount;
  
  // Функция для обновления счетчиков через Context
  const refreshCounters = countersContext?.refresh;


  const loadActiveShifts = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      // Получаем все активные смены для конкретного объекта через специальный API
      // API автоматически проверяет право SESSION_VIEW_ALL и возвращает только активные смены
      const [sessions, executors] = await Promise.all([
        sessionsV2Api.getActiveSiteSessions(siteId),
        siteExecutorsApi.getSiteExecutors(siteId).catch(() => []),
      ]);

      const { byUserId, byExecutorId } = buildExecutorAvatarMaps(executors);
      setAvatarByUserId(byUserId);
      setAvatarByExecutorId(byExecutorId);
      
      // Права на редактирование приходят в том же ответе — не нужны отдельные запросы permissions/edit
      setShiftEditPermissions(prev => {
        const next = new Map(prev);
        sessions.forEach(s => {
          if (s.canEdit !== undefined) next.set(s.id, s.canEdit);
        });
        return next;
      });
      
      // Преобразуем SessionV2 в ActiveShiftV2
      const activeShiftsData = sessions.map(session => ({
        id: session.id,
        userId: session.userId,
        siteExecutorId: session.siteExecutorId,
        siteId: session.siteId,
        timeStart: session.timeStart,
        user: session.userName ? {
          id: session.userId,
          name: session.userName
        } : undefined,
        site: {
          id: session.siteId,
          name: session.siteName || 'Объект'
        },
        typeOfWork: session.typeOfWorkName ? {
          id: session.typeOfWorks,
          name: session.typeOfWorkName
        } : undefined
      }));
      
      setActiveShifts(activeShiftsData);
      
      // Счетчики загружаются через Context в ObjectPage
    } catch (error) {
      console.error('Ошибка при загрузке активных смен:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, siteId]);

  // Счетчики загружаются через Context в ObjectPage (включая unsetActivitiesCount одним запросом)

  // Проверка прав на создание смен
  const checkCreatePermission = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setCheckingPermissions(true);
      const result = await sessionsV2Api.checkCreatePermission(siteId);
      setCanCreateSessions(result.canCreate);
    } catch (error) {
      console.error('Ошибка при проверке прав на создание смен:', error);
      setCanCreateSessions(false);
    } finally {
      setCheckingPermissions(false);
    }
  }, [user?.id, siteId]);

  // Проверка прав на редактирование смены. Возвращает canEdit, чтобы не дублировать запрос при клике.
  const checkEditPermission = useCallback(async (sessionId: number): Promise<boolean> => {
    if (!user?.id || checkingEditPermissions.has(sessionId)) return false;
    
    try {
      setCheckingEditPermissions(prev => new Set(prev).add(sessionId));
      const result = await sessionsV2Api.checkEditPermission(siteId, sessionId);
      setShiftEditPermissions(prev => {
        const newMap = new Map(prev);
        newMap.set(sessionId, result.canEdit);
        return newMap;
      });
      return result.canEdit;
    } catch (error) {
      console.error(`Ошибка при проверке прав на редактирование смены ${sessionId}:`, error);
      setShiftEditPermissions(prev => {
        const newMap = new Map(prev);
        newMap.set(sessionId, false);
        return newMap;
      });
      return false;
    } finally {
      setCheckingEditPermissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  }, [user?.id, siteId]);

  // Обработчик клика на смену для раскрытия
  const handleShiftClick = useCallback(async (shiftId: number) => {
    // Проверяем права на редактирование
    const hasPermission = shiftEditPermissions.get(shiftId);
    if (hasPermission === undefined) {
      // Если права еще не проверены — один запрос, результат используем для раскрытия
      const canEdit = await checkEditPermission(shiftId);
      if (canEdit) {
        setExpandedShiftId(shiftId);
        // Загружаем данные смены
        const session = await sessionsV2Api.getSiteSession(siteId, shiftId);
        setSelectedSession(session);
        // Загружаем активности
        setLoadingSelectedSessionActivities(true);
        try {
          const activities = await sessionsV2Api.getSessionActivities(siteId, shiftId);
          setSelectedSessionActivities(activities);
        } catch (error) {
          console.error('Ошибка загрузки активностей:', error);
          setSelectedSessionActivities([]);
        } finally {
          setLoadingSelectedSessionActivities(false);
        }
      }
    } else if (hasPermission) {
      // Если права уже проверены и есть, переключаем раскрытие
      if (expandedShiftId === shiftId) {
        setExpandedShiftId(null);
        setSelectedSession(null);
        setSelectedSessionActivities([]);
      } else {
        setExpandedShiftId(shiftId);
        // Загружаем данные смены
        const session = await sessionsV2Api.getSiteSession(siteId, shiftId);
        setSelectedSession(session);
        // Загружаем активности
        setLoadingSelectedSessionActivities(true);
        try {
          const activities = await sessionsV2Api.getSessionActivities(siteId, shiftId);
          setSelectedSessionActivities(activities);
        } catch (error) {
          console.error('Ошибка загрузки активностей:', error);
          setSelectedSessionActivities([]);
        } finally {
          setLoadingSelectedSessionActivities(false);
        }
      }
    }
  }, [shiftEditPermissions, expandedShiftId, siteId, checkEditPermission]);

  // Обработчик выделения смены
  const handleShiftToggle = useCallback((shiftId: number) => {
    setSelectedShiftIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(shiftId)) {
        newSet.delete(shiftId);
      } else {
        newSet.add(shiftId);
      }
      return newSet;
    });
  }, []);

  // Обработчик выделения всех смен
  const handleSelectAll = useCallback(() => {
    const allShiftIds = new Set(activeShifts.map(s => s.id));
    const allSelected = activeShifts.length > 0 && activeShifts.every(s => selectedShiftIds.has(s.id));
    if (allSelected) {
      setSelectedShiftIds(new Set());
      setShowActionsMenuBar(false);
    } else {
      setSelectedShiftIds(allShiftIds);
      if (allShiftIds.size > 0) {
        setShowActionsMenuBar(true);
      }
    }
  }, [activeShifts, selectedShiftIds]);

  // Обработчик добавления контрольной точки
  const handleAddCheckpoint = useCallback(async () => {
    if (!selectedSession?.id) return;
    
    setIsAddingCheckpoint(true);
    try {
      // Закрываем текущую активную активность и создаем новую
      const existingActivities = await sessionsV2Api.getSessionActivities(siteId, selectedSession.id);
      const activeActivities = existingActivities.filter(a => !a.timeEnd);
      
      const now = new Date();
      
      // Закрываем все активные активности
      for (const currentActivity of activeActivities) {
        const updateData: UpdateSessionActivityRequest = {
          typeOfWorkId: currentActivity.typeOfWorkId,
          timeStart: currentActivity.timeStart,
          timeEnd: now.toISOString(),
          rate: currentActivity.rate || 0,
          contractId: currentActivity.contractId,
          comment: currentActivity.comment || ''
        };
        await sessionsV2Api.updateSessionActivity(siteId, selectedSession.id, currentActivity.id, updateData);
      }
      
      // Создаем новую активную активность
      const newActivityData: CreateSessionActivityRequest = {
        typeOfWorkId: 1, // "Не указано" по умолчанию
        timeStart: now.toISOString(),
        timeEnd: undefined, // Открытая активность
        rate: 0,
        contractId: undefined,
        comment: ''
      };
      await sessionsV2Api.createSessionActivity(siteId, selectedSession.id, newActivityData);
      
      showSuccess('Контрольная точка добавлена');
      // Перезагружаем активности
      const activities = await sessionsV2Api.getSessionActivities(siteId, selectedSession.id);
      setSelectedSessionActivities(activities);
      await loadActiveShifts();
    } catch (error: any) {
      console.error('Ошибка при добавлении контрольной точки:', error);
      let errorMessage = 'Ошибка при добавлении контрольной точки. Попробуйте еще раз.';
      if (error?.response) {
        const responseData = error.response.data;
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showError(errorMessage);
    } finally {
      setIsAddingCheckpoint(false);
    }
  }, [selectedSession, siteId, showSuccess, showError, loadActiveShifts]);

  // Обработчик закрытия смены
  const handleEndShift = useCallback(async () => {
    if (!selectedSession?.id) return;
    
    setIsEnding(true);
    try {
      const timeEnd = new Date().toISOString();
      const sessionData: UpdateSessionV2Request = {
        timeStart: selectedSession.timeStart,
        closeSession: true,
        timeEnd: timeEnd,
        rate: selectedSession.rate,
        contractId: selectedSession.contractId
      };
      
      await sessionsV2Api.updateSession(siteId, selectedSession.id, sessionData);
      showSuccess('Смена завершена');
      setExpandedShiftId(null);
      setSelectedSession(null);
      setSelectedSessionActivities([]);
      await loadActiveShifts();
      refreshCounters?.();
    } catch (error: any) {
      console.error('Ошибка при завершении смены:', error);
      showError(error?.response?.data || 'Ошибка при завершении смены. Попробуйте еще раз.');
    } finally {
      setIsEnding(false);
    }
  }, [selectedSession, siteId, showSuccess, showError, loadActiveShifts, refreshCounters]);

  // Обработчик открытия модального окна для заполнения активностей
  const handleOpenActivitiesSheet = useCallback((session: SessionV2) => {
    setSessionForActivities(session);
    setShowActivitiesSheet(true);
  }, []);

  // Проверяем права при загрузке компонента
  useEffect(() => {
    checkCreatePermission();
  }, [checkCreatePermission]);

  // Загружаем активные смены для превью и fullscreen; обновления — через SignalR
  useEffect(() => {
    loadActiveShifts();
  }, [loadActiveShifts]);

  // Запрос permissions/edit только для смен, добавленных не из GET active (например через SignalR), у которых ещё нет canEdit. Без shiftEditPermissions в deps — иначе при каждом ответе эффект перезапускается и дублирует запросы.
  useEffect(() => {
    if ((!isCollapsed || isFullscreenOpen) && activeShifts.length > 0 && user?.id) {
      activeShifts.forEach(shift => {
        if (shiftEditPermissions.get(shift.id) === undefined) {
          checkEditPermission(shift.id);
        }
      });
    }
  }, [activeShifts, isCollapsed, isFullscreenOpen, user?.id, checkEditPermission]);

  // Обновляем время для таймера каждую минуту
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Обновляем каждую минуту
    
    return () => {
      clearInterval(timeInterval);
    };
  }, []);

  // Обработчики SignalR событий для обновления смен в реальном времени
  const handleSessionCreated = useCallback((session: SessionEvent) => {
    // Сразу после массового открытия не добавляем из SignalR — список заполнит GET active с canEdit, без лишних запросов permissions/edit
    if (skipSignalRAddRef.current) return;
    // Проверяем, что смена относится к текущему объекту и активна (без timeEnd)
    if (session.siteId === siteId && !session.timeEnd) {
      // Добавляем новую смену в список
      setActiveShifts(prev => {
        // Проверяем, нет ли уже такой смены
        if (prev.some(s => s.id === session.id)) {
          return prev;
        }
        
        // Преобразуем SessionEvent в ActiveShiftV2
        const newShift: ActiveShiftV2 = {
          id: session.id,
          userId: session.userId,
          siteId: session.siteId,
          timeStart: session.timeStart,
          user: session.userName ? {
            id: session.userId,
            name: session.userName
          } : undefined,
          site: {
            id: session.siteId,
            name: session.siteName || 'Объект'
          },
          typeOfWork: session.typeOfWorkName ? {
            id: session.typeOfWorks,
            name: session.typeOfWorkName
          } : undefined
        };
        
        return [...prev, newShift];
      });
      
      // Обновляем счетчики через Context
      refreshCounters?.();
    }
  }, [siteId, refreshCounters]);

  const handleSessionUpdated = useCallback((session: SessionEvent) => {
    // Обновляем смену или добавляем, если она активна и относится к текущему объекту
    if (session.siteId === siteId) {
      if (!session.timeEnd) {
        // Смена активна - обновляем или добавляем
        setActiveShifts(prev => {
          const existingIndex = prev.findIndex(s => s.id === session.id);
          const updatedShift: ActiveShiftV2 = {
            id: session.id,
            userId: session.userId,
            siteId: session.siteId,
            timeStart: session.timeStart,
            user: session.userName ? {
              id: session.userId,
              name: session.userName
            } : undefined,
            site: {
              id: session.siteId,
              name: session.siteName || 'Объект'
            },
            typeOfWork: session.typeOfWorkName ? {
              id: session.typeOfWorks,
              name: session.typeOfWorkName
            } : undefined
          };
          
          if (existingIndex >= 0) {
            // Обновляем существующую смену
            const updated = [...prev];
            updated[existingIndex] = updatedShift;
            return updated;
          } else {
            // Добавляем новую смену
            return [...prev, updatedShift];
          }
        });
      } else {
        // Смена закрыта - удаляем из списка активных (если не идёт массовое закрытие — тогда список обновит GET active)
        if (!skipSignalRRemoveRef.current) {
          setActiveShifts(prev => prev.filter(s => s.id !== session.id));
        }
      }
      
      // Обновляем счетчики через Context
      refreshCounters?.();
    }
  }, [siteId, refreshCounters]);

  const handleSessionClosed = useCallback((session: SessionEvent) => {
    if (skipSignalRRemoveRef.current) return;
    // Удаляем закрытую смену из списка активных
    if (session.siteId === siteId) {
      setActiveShifts(prev => prev.filter(s => s.id !== session.id));
      // Обновляем счетчики через Context
      refreshCounters?.();
    }
  }, [siteId, refreshCounters]);

  const handleSessionDeleted = useCallback((session: SessionEvent) => {
    // Удаляем удаленную смену из списка активных
    if (session.siteId === siteId) {
      setActiveShifts(prev => prev.filter(s => s.id !== session.id));
      // Обновляем счетчики через Context
      refreshCounters?.();
    }
  }, [siteId, refreshCounters]);

  // Подписываемся на SignalR события для данного объекта
  // Используем уникальный subscriptionId для этого компонента
  useOptimizedSignalR({
    onSessionCreated: handleSessionCreated,
    onSessionUpdated: handleSessionUpdated,
    onSessionClosed: handleSessionClosed,
    onSessionDeleted: handleSessionDeleted
  }, {
    siteId,
    subscriptionId: `activeshifts_site_${siteId}`
  });

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Расчет и форматирование наработанного времени
  const calculateElapsedTime = (timeStart: string): string => {
    const startTime = new Date(timeStart);
    const now = currentTime;
    const diffMs = now.getTime() - startTime.getTime();
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}ч ${minutes}м`;
    } else {
      return `${minutes}м`;
    }
  };

  const getTotalActiveEmployees = () => {
    return activeShifts.length;
  };

  // Группировка смен по типу работы
  const groupShiftsByTypeOfWork = () => {
    const typeMap = new Map<number, { typeOfWorkName: string; shifts: ActiveShiftV2[] }>();
    
    activeShifts.forEach(shift => {
      const typeId = shift.typeOfWork?.id || 0;
      const typeName = shift.typeOfWork?.name || 'Неизвестный вид работ';
      
      if (!typeMap.has(typeId)) {
        typeMap.set(typeId, {
          typeOfWorkName: typeName,
          shifts: []
        });
      }
      
      typeMap.get(typeId)!.shifts.push(shift);
    });
    
    return Array.from(typeMap.values());
  };

  // Подготовка данных для StatsGrid
  const statsData: StatItem[] = [
    {
      id: 'employees',
      value: getTotalActiveEmployees(),
      label: 'Сотрудников',
      icon: 'ri-group-line',
      color: 'muted'
    }
  ];

  // Группировка смен по типу работы (мемоизируется)
  const groupedShifts = React.useMemo(() => {
    return groupShiftsByTypeOfWork();
  }, [activeShifts]);

  // Мемоизация вычислений Timeline для раскрытой карточки смены
  const expandedTimelineData = React.useMemo(() => {
    if (!selectedSession || selectedSessionActivities.length === 0) {
      return null;
    }

    const sessionStart = new Date(selectedSession.timeStart);
    const sessionEnd = selectedSession.timeEnd 
      ? new Date(selectedSession.timeEnd) 
      : new Date();

    // Сортируем по sortOrder, затем по timeStart (для режима по часам timeStart может отсутствовать)
    const sortedActivities = [...selectedSessionActivities].sort((a, b) => {
      const oA = a.sortOrder ?? 999999;
      const oB = b.sortOrder ?? 999999;
      if (oA !== oB) return oA - oB;
      const tA = a.timeStart ? new Date(a.timeStart).getTime() : 0;
      const tB = b.timeStart ? new Date(b.timeStart).getTime() : 0;
      return tA - tB;
    });

    const hasHoursOnly = sortedActivities.some(a => !a.timeStart && (a.allocatedHours != null || (a.hours != null && a.hours > 0)));

    const timelineSegments: TimelineSegment[] = buildSessionTimelineSegments(
      sortedActivities,
      selectedSession.timeStart,
      selectedSession.timeEnd
    ).map((segment) => ({
      ...segment,
      label: segment.label || 'Не указано'
    }));

    // Ручки между сегментами (только для time-based, в часах-режиме не поддерживаем drag)
    const timelineHandles: TimelineHandle[] = [];
    if (!hasHoursOnly) {
      for (let i = 0; i < sortedActivities.length - 1; i++) {
        const nextActivity = sortedActivities[i + 1];
        if (nextActivity.timeStart) {
          timelineHandles.push({
            id: `handle-${i}`,
            time: new Date(nextActivity.timeStart)
          });
        }
      }
    }

    return {
      sessionStart,
      sessionEnd,
      timelineSegments,
      timelineHandles
    };
  }, [selectedSession, selectedSessionActivities]);

  // Функции форматирования для раскрытой карточки (мемоизируются один раз)
  const formatTimeForExpanded = React.useCallback((date: Date) => {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }, []);

  const formatDurationForExpanded = React.useCallback((hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (m === 0) return `${h}ч`;
    return `${h}ч ${m}м`;
  }, []);

  // Подготовка данных для DataList с группировкой по типу работы
  // Пересчитывается при изменении activeShifts или currentTime
  const dataListItems: DataListItem[] = React.useMemo(() => {
    const items: DataListItem[] = [];
    
    groupedShifts.forEach((group, groupIndex) => {
      // Добавляем заголовок группы с типом работы
      items.push({
        id: `type-${groupIndex}`,
        title: group.typeOfWorkName,
        subtitle: `${group.shifts.length} чел.`,
        isHeader: true,
        onContextMenu: (e) => {
          // Убираем стандартное контекстное меню браузера при долгом нажатии
          e.preventDefault();
        },
        onClick: () => {} // Заголовки не кликабельны
      });
      
      // Добавляем смены для этого типа работы
      group.shifts.forEach((shift) => {
        const isSelected = selectedShiftIds.has(shift.id);
        const canEdit = shiftEditPermissions.get(shift.id);
        const isExpanded = expandedShiftId === shift.id;
        
        // Создаем содержимое для раскрытой области
        const expandedContent = isExpanded && selectedSession && selectedSession.id === shift.id ? (
          <div 
            style={{ 
              marginTop: '16px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Предупреждение о не указанных активностях */}
            {!loadingSelectedSessionActivities && selectedSessionActivities.some(a => !a.typeOfWorkId || a.typeOfWorkId <= 1) && (
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#2a1a0a', 
                borderRadius: '8px',
                border: '1px solid #ffa500',
                marginBottom: '16px',
                display: 'flex', 
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  color: '#ffa500',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  <i className="ri-alert-line"></i>
                  <span>
                    Требуется указать виды работ ({selectedSessionActivities.filter(a => !a.typeOfWorkId || a.typeOfWorkId <= 1).length} активностей)
                  </span>
                </div>
                <StatusActionButton
                  type="button"
                  variant="warning"
                  icon="ri-edit-line"
                  onClick={() => handleOpenActivitiesSheet(selectedSession)}
                  fullWidth
                >
                  Указать виды работ
                </StatusActionButton>
              </div>
            )}
            
            {/* Горизонтальная временная шкала активностей */}
            {loadingSelectedSessionActivities ? (
              <div style={{ 
                padding: '20px', 
                textAlign: 'center', 
                color: '#888',
                fontSize: '14px'
              }}>
                <i className="ri-loader-4-line ri-spin" style={{ fontSize: '20px', marginRight: '8px' }}></i>
                Загрузка активностей...
              </div>
            ) : selectedSessionActivities.length > 0 && expandedTimelineData ? (
              <div style={{ margin: '0 -16px' }}>
                <Timeline
                  startTime={expandedTimelineData.sessionStart}
                  endTime={expandedTimelineData.sessionEnd}
                  segments={expandedTimelineData.timelineSegments}
                  handles={expandedTimelineData.timelineHandles}
                  formatTime={formatTimeForExpanded}
                  formatDuration={formatDurationForExpanded}
                  minSegmentWidth={8}
                  showTimeLabels={true}
                  showDuration={true}
                  showLabels={true}
                  draggable={false}
                  className="activities-timeline expanded-timeline"
                />
              </div>
            ) : (
              <div style={{ 
                padding: '20px', 
                textAlign: 'center', 
                color: '#888',
                fontSize: '14px'
              }}>
                Нет активностей
              </div>
            )}
          </div>
        ) : undefined;
        
        items.push({
          id: shift.id,
          title: shift.user?.name || 'Неизвестный сотрудник',
          details: [
            {
              icon: 'ri-time-line',
              text: `Начало: ${formatTime(shift.timeStart)}`
            }
          ],
          rightContent: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className={styles.timerContainer}>
                <i className={`ri-timer-line ${styles.timerIcon}`}></i>
                <span className={styles.timerText}>{calculateElapsedTime(shift.timeStart)}</span>
              </div>
              {canEdit && (
                <i 
                  className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line`}
                  style={{ fontSize: '18px', color: '#888', cursor: 'pointer' }}
                ></i>
              )}
            </div>
          ),
          bottomContent: expandedContent,
          isSelected: isSelected,
          onSelect: () => {
            handleShiftToggle(shift.id);
          },
          onClick: canEdit ? () => handleShiftClick(shift.id) : undefined,
          onContextMenu: (e) => {
            // Убираем стандартное контекстное меню браузера при долгом нажатии
            e.preventDefault();
          }
        });
      });
    });
    
    return items;
  }, [groupedShifts, currentTime, selectedShiftIds, formatTime, calculateElapsedTime, shiftEditPermissions, expandedShiftId, handleShiftClick, handleShiftToggle, selectedSession, selectedSessionActivities, loadingSelectedSessionActivities, handleOpenActivitiesSheet]);

  // Вычисляет время окончания активности
  const getActivityEndTime = useCallback((items: ActivityItem[], index: number, sessionEndTime: Date): Date => {
    if (index < items.length - 1) {
      return items[index + 1].timeStart;
    }
    return sessionEndTime;
  }, []);

  // Пересчитывает статистику для всех активностей
  const recalculateStats = useCallback((items: ActivityItem[], currentStart: Date, currentEnd: Date): ActivityItem[] => {
    const sorted = [...items].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const totalSessionDuration = currentEnd.getTime() - currentStart.getTime();
    const MS_IN_HOUR = 3600000;
    
    return sorted.map((item, index) => {
      const endTime = index < sorted.length - 1 ? sorted[index + 1].timeStart : currentEnd;
      const duration = endTime.getTime() - item.timeStart.getTime();
      
      const hours = Math.max(0, duration / MS_IN_HOUR);
      const percentage = totalSessionDuration > 0 ? (duration / totalSessionDuration) * 100 : 0;
      const amount = hours * item.rate;
      
      return {
        ...item,
        hours,
        percentage,
        amount
      };
    });
  }, []);

  // Форматирование времени
  const formatTimeCallback = useCallback((date: Date) => {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }, []);

  const formatDurationAsObject = useCallback((hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return { hours: h, minutes: m };
  }, []);

  // Инициализация формы при открытии модального окна для заполнения активностей
  useEffect(() => {
    if (sessionForActivities && showActivitiesSheet) {
      if (sessionForActivities.timeEnd) {
        const endTime = new Date(sessionForActivities.timeEnd);
        // Форматируем локальное время для input[type="datetime-local"]
        const year = endTime.getFullYear();
        const month = String(endTime.getMonth() + 1).padStart(2, '0');
        const day = String(endTime.getDate()).padStart(2, '0');
        const hours = String(endTime.getHours()).padStart(2, '0');
        const minutes = String(endTime.getMinutes()).padStart(2, '0');
        setTimeEnd(`${year}-${month}-${day}T${hours}:${minutes}`);
      } else {
        const now = new Date();
        // Форматируем локальное время для input[type="datetime-local"]
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        setTimeEnd(`${year}-${month}-${day}T${hours}:${minutes}`);
      }
      
      const loadExistingActivities = async () => {
        try {
          const existingActivities = await sessionsV2Api.getSessionActivities(siteId, sessionForActivities.id);
          
          if (existingActivities.length > 0) {
            const sorted = [...existingActivities].sort((a, b) => {
              const oA = a.sortOrder ?? 999999;
              const oB = b.sortOrder ?? 999999;
              if (oA !== oB) return oA - oB;
              const tA = a.timeStart ? new Date(a.timeStart).getTime() : 0;
              const tB = b.timeStart ? new Date(b.timeStart).getTime() : 0;
              return tA - tB;
            });
            const sessionStart = new Date(sessionForActivities.timeStart);
            let cumulativeMs = sessionStart.getTime();
            const items: ActivityItem[] = sorted.map((activity) => {
              const isHoursOnly = !activity.timeStart && (activity.allocatedHours != null || activity.hours != null);
              let start: Date;
              if (activity.timeStart) {
                start = new Date(activity.timeStart);
              } else {
                start = new Date(cumulativeMs);
                const h = activity.hours ?? activity.allocatedHours ?? 0;
                cumulativeMs += h * 60 * 60 * 1000;
              }
              return {
                id: activity.id,
                typeOfWorkId: activity.typeOfWorkId || 1,
                typeOfWorkName: activity.typeOfWorkName,
                contractId: activity.contractId,
                contractTitle: activity.contractTitle,
                timeStart: start,
                rate: activity.rate,
                hours: activity.hours ?? activity.allocatedHours ?? 0,
                percentage: 0,
                amount: 0,
                isNew: false,
                isModified: false,
                comment: activity.comment || '',
                isHoursOnly
              };
            });
            const sessionEnd = sessionForActivities.timeEnd ? new Date(sessionForActivities.timeEnd) : new Date();
            const withStats = recalculateStats(items, sessionStart, sessionEnd);
            setActivities(withStats);
          } else {
            setActivities([]);
          }
        } catch (error) {
          console.error('Ошибка загрузки активностей:', error);
          setActivities([]);
        }
      };
      loadExistingActivities();
    }
  }, [sessionForActivities, showActivitiesSheet, siteId, recalculateStats]);

  // Пересчитываем статистику активностей при изменении времени
  useEffect(() => {
    if (activities.length > 0 && sessionForActivities) {
      const sessionStartTime = new Date(sessionForActivities.timeStart);
      const sessionEndTime = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? new Date(sessionForActivities.timeEnd) : new Date());
      setActivities(prev => recalculateStats(prev, sessionStartTime, sessionEndTime));
    }
  }, [timeEnd, sessionForActivities, recalculateStats]);

  // Загружаем виды работ при открытии модального окна выбора
  useEffect(() => {
    if (showWorkTypeSelector && activitySourceTab === 'ставка') {
      const loadTypesOfWork = async () => {
        setLoadingWorkTypes(true);
        try {
          const data = await typesOfWorkV2Api.getTypesOfWork();
          setTypesOfWork(data);
        } catch (error) {
          console.error('Ошибка загрузки видов работ:', error);
          showError('Ошибка загрузки видов работ');
        } finally {
          setLoadingWorkTypes(false);
        }
      };
      loadTypesOfWork();
    }
  }, [showWorkTypeSelector, activitySourceTab, showError]);

  // Загружаем контракты при переключении на вкладку «Сделка»
  useEffect(() => {
    if (showWorkTypeSelector && activitySourceTab === 'сделка' && siteId) {
      const loadContracts = async () => {
        setLoadingContracts(true);
        try {
          const data = await contractsV2Api.getSiteContracts(siteId, { onlyActive: true });
          setContracts(data);
        } catch (error) {
          console.error('Ошибка загрузки контрактов:', error);
          showError('Ошибка загрузки контрактов');
        } finally {
          setLoadingContracts(false);
        }
      };
      loadContracts();
    }
  }, [showWorkTypeSelector, activitySourceTab, siteId, showError]);

  // Равномерно распределяет активности
  const distributeActivitiesEvenly = (
    items: typeof activities, 
    availableStart: Date, 
    availableEnd: Date
  ) => {
    if (items.length === 0) return items;
    
    const MIN_DURATION = 60 * 1000;
    const totalAvailable = availableEnd.getTime() - availableStart.getTime();
    const durationPerActivity = Math.max(MIN_DURATION, totalAvailable / items.length);
    
    return items.map((item, index) => {
      const newStartTime = availableStart.getTime() + (durationPerActivity * index);
      return {
        ...item,
        timeStart: new Date(newStartTime),
        isModified: true
      };
    });
  };

  // Открывает модальное окно для выбора видов работ
  const handleAddActivity = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingActivityId(null);
    setActivitySourceTab('ставка');
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    setShowWorkTypeSelector(true);
  };

  const handleOpenActivityTypeEditor = useCallback((item: ActivityItem) => {
    setEditingActivityId(item.id);
    if (item.contractId) {
      setActivitySourceTab('сделка');
      setSelectedContractIds([item.contractId]);
      setSelectedWorkTypeIds([]);
    } else {
      setActivitySourceTab('ставка');
      setSelectedWorkTypeIds(item.typeOfWorkId ? [item.typeOfWorkId] : []);
      setSelectedContractIds([]);
    }
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    setShowWorkTypeSelector(true);
  }, []);

  // Переключает выбор вида работы
  const toggleWorkTypeSelection = (typeId: number) => {
    setSelectedWorkTypeIds(prev => {
      if (editingActivityId !== null) return prev[0] === typeId ? [] : [typeId];
      if (prev.includes(typeId)) return prev.filter(id => id !== typeId);
      return [...prev, typeId];
    });
  };

  const toggleContractSelection = (contractId: number) => {
    setSelectedContractIds(prev => {
      if (editingActivityId !== null) return prev[0] === contractId ? [] : [contractId];
      if (prev.includes(contractId)) return prev.filter(id => id !== contractId);
      return [...prev, contractId];
    });
  };

  // Подтверждает выбор видов работ/контрактов и создает или обновляет активности
  const handleConfirmWorkTypeSelection = () => {
    const isDealMode = activitySourceTab === 'сделка';
    const itemsCount = isDealMode ? selectedContractIds.length : selectedWorkTypeIds.length;
    if (itemsCount === 0) {
      showError(isDealMode ? 'Выберите хотя бы один контракт' : 'Выберите хотя бы один вид работы');
      return;
    }

    if (!sessionForActivities) {
      showError('Смена не найдена');
      return;
    }

    if (editingActivityId !== null) {
      const contractId = isDealMode ? selectedContractIds[0] : undefined;
      const typeId = isDealMode ? undefined : selectedWorkTypeIds[0];
      if (isDealMode && contractId) {
        const contract = contracts.find(c => c.id === contractId);
        if (contract) {
          updateActivity(editingActivityId, { typeOfWorkId: contract.typeOfWorkId, contractId: contract.id, contractTitle: contract.title });
        }
      } else if (!isDealMode && typeId) {
        updateActivity(editingActivityId, { typeOfWorkId: typeId, contractId: undefined, contractTitle: undefined });
      }
      setShowWorkTypeSelector(false);
      setEditingActivityId(null);
      setSelectedWorkTypeIds([]);
      setSelectedContractIds([]);
      setWorkTypeSearchQuery('');
      setContractSearchQuery('');
      setSelectedWorkTypeCategory(null);
      showSuccess('Тип активности изменён');
      return;
    }

    const currentStart = new Date(sessionForActivities.timeStart);
    const currentEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? new Date(sessionForActivities.timeEnd) : new Date());
    
    const sorted = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const newActivities: typeof activities = [];
    const timestamp = Date.now();
    
    let nextActivityStartTime: number;
    
    if (sorted.length === 0) {
      nextActivityStartTime = currentStart.getTime();
    } else {
      const lastActivity = sorted[sorted.length - 1];
      const lastActivityEnd = getActivityEndTime(sorted, sorted.length - 1, currentEnd);
      nextActivityStartTime = lastActivityEnd.getTime() + 1000;
    }
    
    const availableTime = currentEnd.getTime() - nextActivityStartTime;
    const MIN_DURATION = 60 * 1000;
    const allHoursOnly = sorted.every(a => a.isHoursOnly);

    if (isDealMode) {
      const selectedContracts = contracts.filter(c => selectedContractIds.includes(c.id));
      const n = selectedContracts.length;
      if (availableTime < MIN_DURATION * n) {
        selectedContracts.forEach((contract, index) => {
          newActivities.push({
            id: `new_${timestamp}_${index}`,
            typeOfWorkId: contract.typeOfWorkId,
            contractId: contract.id,
            contractTitle: contract.title,
            timeStart: new Date(nextActivityStartTime),
            rate: 0,
            hours: 0,
            percentage: 0,
            amount: 0,
            isNew: true,
            isModified: false,
            isHoursOnly: allHoursOnly
          });
        });
        const allActivities = [...sorted, ...newActivities];
        setActivities(recalculateStats(distributeActivitiesEvenly(allActivities, currentStart, currentEnd), currentStart, currentEnd));
      } else {
        const durationPerActivity = Math.max(MIN_DURATION, availableTime / n);
        selectedContracts.forEach((contract, index) => {
          const activityStartTime = nextActivityStartTime + (durationPerActivity * index);
          newActivities.push({
            id: `new_${timestamp}_${index}`,
            typeOfWorkId: contract.typeOfWorkId,
            contractId: contract.id,
            contractTitle: contract.title,
            timeStart: new Date(activityStartTime),
            rate: 0,
            hours: 0,
            percentage: 0,
            amount: 0,
            isNew: true,
            isModified: false,
            isHoursOnly: allHoursOnly
          });
        });
        setActivities(recalculateStats([...sorted, ...newActivities], currentStart, currentEnd));
      }
    } else if (availableTime < MIN_DURATION * selectedWorkTypeIds.length) {
      selectedWorkTypeIds.forEach((typeId, index) => {
        newActivities.push({
          id: `new_${timestamp}_${index}`,
          typeOfWorkId: typeId,
          comment: '',
          timeStart: new Date(nextActivityStartTime),
          rate: sessionForActivities?.rate || 0,
          hours: 0,
          percentage: 0,
          amount: 0,
          isNew: true,
          isModified: false,
          isHoursOnly: allHoursOnly
        });
      });
      
      const allActivities = [...sorted, ...newActivities];
      const redistributed = distributeActivitiesEvenly(allActivities, currentStart, currentEnd);
      const withStats = recalculateStats(redistributed, currentStart, currentEnd);
      
      setActivities(withStats);
    } else {
      const durationPerActivity = Math.max(MIN_DURATION, availableTime / selectedWorkTypeIds.length);
      
      selectedWorkTypeIds.forEach((typeId, index) => {
        const activityStartTime = nextActivityStartTime + (durationPerActivity * index);
        newActivities.push({
          id: `new_${timestamp}_${index}`,
          typeOfWorkId: typeId,
          timeStart: new Date(activityStartTime),
          rate: sessionForActivities?.rate || 0,
          hours: 0,
          percentage: 0,
          amount: 0,
          isNew: true,
          isModified: false,
          comment: '',
          isHoursOnly: allHoursOnly
        });
      });
      
      const allActivities = [...sorted, ...newActivities];
      const withStats = recalculateStats(allActivities, currentStart, currentEnd);
      
      setActivities(withStats);
    }
    
    setShowWorkTypeSelector(false);
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    
    showSuccess(`Добавлено активностей: ${newActivities.length}`);
  };

  // Закрывает модальное окно выбора видов работ
  const handleCloseWorkTypeSelector = () => {
    setShowWorkTypeSelector(false);
    setEditingActivityId(null);
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
  };

  // Удаление активности
  const handleDeleteActivity = useCallback((id: number | string) => {
    if (!sessionForActivities) return;
    const currentStart = new Date(sessionForActivities.timeStart);
    const currentEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? new Date(sessionForActivities.timeEnd) : new Date());
    setActivities(prev => {
      const updated = prev.filter(a => a.id !== id);
      const final = recalculateStats(updated, currentStart, currentEnd);
      return final;
    });
    showSuccess('Активность удалена');
  }, [sessionForActivities, timeEnd, recalculateStats, showSuccess]);

  // Обновляет активность
  const updateActivity = useCallback((id: number | string, updates: Partial<ActivityItem>) => {
    if (!sessionForActivities) return;
    const currentStart = new Date(sessionForActivities.timeStart);
    const currentEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? new Date(sessionForActivities.timeEnd) : new Date());
    
    setActivities(prev => {
      const sorted = [...prev].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      const currentIndex = sorted.findIndex(item => item.id === id);
      
      if (currentIndex === -1) {
        return prev;
      }
      
      const updated = [...sorted];
      let hasUpdates = false;
      const updateObject: Partial<ActivityItem> = {};
      
      if (updates.timeStart) {
        const newStart = updates.timeStart.getTime();
        
        const minStart = currentIndex > 0 
          ? sorted[currentIndex - 1].timeStart.getTime() + 60000
          : currentStart.getTime();
        
        const maxStart = currentIndex < sorted.length - 1
          ? sorted[currentIndex + 1].timeStart.getTime() - 60000
          : currentEnd.getTime() - 60000;
        
        const clampedStart = Math.max(minStart, Math.min(newStart, maxStart));
        updateObject.timeStart = new Date(clampedStart);
        hasUpdates = true;
      }
      
      if (updates.typeOfWorkId !== undefined) {
        updateObject.typeOfWorkId = updates.typeOfWorkId;
        hasUpdates = true;
      }
      if (updates.contractId !== undefined) {
        updateObject.contractId = updates.contractId;
        hasUpdates = true;
      }
      if (updates.contractTitle !== undefined) {
        updateObject.contractTitle = updates.contractTitle;
        hasUpdates = true;
      }
      
      if (updates.comment !== undefined) {
        updateObject.comment = updates.comment;
        hasUpdates = true;
      }
      
      if (hasUpdates) {
        updated[currentIndex] = {
          ...updated[currentIndex],
          ...updateObject,
          isModified: true
        };
      }
      
      return recalculateStats(updated, currentStart, currentEnd);
    });
  }, [sessionForActivities, timeEnd, recalculateStats]);

  // Обработчик сохранения активностей для закрытой смены
  const handleSaveActivities = async () => {
    if (!sessionForActivities?.id) {
      showError('Смена не найдена');
      return;
    }

    if (activities.length === 0) {
      showError('Добавьте хотя бы одну активность');
      return;
    }

    const incompleteActivities = activities.filter(a => !a.contractId && (!a.typeOfWorkId || a.typeOfWorkId === 0));
    if (incompleteActivities.length > 0) {
      showError('Выберите вид работы или контракт для всех активностей');
      return;
    }

    const sessionStart = new Date(sessionForActivities.timeStart);
    const sessionEnd = sessionForActivities.timeEnd ? new Date(sessionForActivities.timeEnd) : new Date();
    const sortedForValidation = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    
    for (let i = 0; i < sortedForValidation.length; i++) {
      const activity = sortedForValidation[i];
      if (activity.isHoursOnly) continue;
      if (activity.timeStart.getTime() < sessionStart.getTime()) {
        showError('Время начала активности не может быть раньше начала смены');
        return;
      }
      const activityEnd = getActivityEndTime(sortedForValidation, i, sessionEnd);
      if (activityEnd.getTime() > sessionEnd.getTime()) {
        showError('Время окончания активности не может быть позже окончания смены');
        return;
      }
    }

    setIsEnding(true);
    try {
      const sortedActivities = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      
      const activitiesData: SessionActivityUpdateRequest[] = sortedActivities.map((item, index) => {
        if (item.isHoursOnly) {
          return {
            id: typeof item.id === 'number' && item.id > 0 ? item.id : undefined,
            typeOfWorkId: item.typeOfWorkId,
            timeStart: undefined,
            timeEnd: undefined,
            allocatedHours: item.hours,
            sortOrder: index,
            rate: item.rate || 0,
            contractId: item.contractId,
            comment: item.comment || ''
          };
        }
        const isFirstActivity = index === 0;
        const isLastActivity = index === sortedActivities.length - 1;
        const isSessionClosed = !!sessionForActivities.timeEnd;
        
        const activityStartTime = isFirstActivity ? sessionStart : item.timeStart;
        const endTime = isLastActivity ? sessionEnd : getActivityEndTime(sortedActivities, index, sessionEnd);
        const activityId = typeof item.id === 'number' && item.id > 0 ? item.id : undefined;
        const activityEndTime = (isLastActivity && !isSessionClosed) ? undefined : endTime.toISOString();
        
        return {
          id: activityId,
          typeOfWorkId: item.typeOfWorkId,
          timeStart: activityStartTime.toISOString(),
          timeEnd: activityEndTime,
          rate: item.rate || 0,
          contractId: item.contractId,
          comment: item.comment || ''
        };
      });

      const sessionData: UpdateSessionV2Request = {
        timeStart: sessionForActivities.timeStart,
        closeSession: !!sessionForActivities.timeEnd,
        ...(sessionForActivities.timeEnd ? { timeEnd: sessionForActivities.timeEnd } : {}),
        activities: activitiesData
      };

      await sessionsV2Api.updateSession(siteId, sessionForActivities.id, sessionData);
      
      showSuccess('Активности успешно сохранены');
      
      setShowActivitiesSheet(false);
      setSessionForActivities(null);
      setActivities([]);
      
      // Обновляем данные
      if (expandedShiftId === sessionForActivities.id) {
        const session = await sessionsV2Api.getSiteSession(siteId, sessionForActivities.id);
        setSelectedSession(session);
        const activities = await sessionsV2Api.getSessionActivities(siteId, sessionForActivities.id);
        setSelectedSessionActivities(activities);
      }
      await loadActiveShifts();
    } catch (error: any) {
      console.error('Ошибка при сохранении активностей:', error);
      const errorMessage = error?.response?.data || error?.message || 'Ошибка при сохранении активностей. Попробуйте еще раз.';
      showError(errorMessage);
    } finally {
      setIsEnding(false);
    }
  };

  // Обработчик закрытия bottom sheet для заполнения активностей
  const handleCloseActivitiesSheet = () => {
    setShowActivitiesSheet(false);
    setSessionForActivities(null);
    setActivities([]);
  };

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
  }, [activities]);

  // Получаем времена пересечений (между активностями)
  const intersectionTimes = useMemo((): Date[] => {
    if (sortedActivities.length < 2 || !sessionForActivities) return [];
    return sortedActivities.slice(1).map((activity, arrayIndex) => {
      const handleIndex = arrayIndex;
      if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
        return tempTimelinePositions.get(handleIndex)!;
      }
      return activity.timeStart;
    });
  }, [sortedActivities, tempTimelinePositions, sessionForActivities]);

  // Получаем информацию о сегментах временной шкалы
  const timelineSegments = useMemo((): Array<{ start: Date; end: Date; activity: ActivityItem; duration?: number }> => {
    if (sortedActivities.length === 0 || !sessionForActivities) return [];
    
    const currentStart = new Date(sessionForActivities.timeStart);
    const currentEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? new Date(sessionForActivities.timeEnd) : new Date());
    
    return sortedActivities.map((activity, index) => {
      let start: Date;
      let end: Date;
      
      // Начало активности - всегда её реальное время начала
      start = activity.timeStart;
      
      if (index < sortedActivities.length - 1) {
        const handleIndex = index;
        if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
          end = tempTimelinePositions.get(handleIndex)!;
        } else {
          end = sortedActivities[index + 1].timeStart;
        }
      } else {
        end = currentEnd;
      }
      
      const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      return { start, end, activity, duration };
    });
  }, [sortedActivities, sessionForActivities, timeEnd, tempTimelinePositions]);

  // Фильтруем типы работ для модального окна
  const filteredTypes = typesOfWork.filter(type => {
    const matchesSearch = type.name.toLowerCase().includes(workTypeSearchQuery.toLowerCase()) ||
                         type.section.toLowerCase().includes(workTypeSearchQuery.toLowerCase());
    const matchesCategory = !selectedWorkTypeCategory || type.section === selectedWorkTypeCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedTypes = filteredTypes.reduce((acc, type) => {
    if (!acc[type.section]) {
      acc[type.section] = [];
    }
    acc[type.section].push(type);
    return acc;
  }, {} as Record<string, TypeOfWorkV2[]>);

  const filteredContracts = useMemo(() =>
    contracts.filter(c =>
      c.title.toLowerCase().includes(contractSearchQuery.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(contractSearchQuery.toLowerCase())
    ),
    [contracts, contractSearchQuery]
  );

  // Обработчик массового открытия смен
  const handleBulkOpenSessions = useCallback(async (userIds: number[]) => {
    if (userIds.length === 0) return;

    try {
      setBulkCreating(true);
      // Включаем до вызова bulk: SignalR приходит раньше ответа bulk, без этого добавим смены без canEdit и уйдут запросы permissions/edit
      skipSignalRAddRef.current = true;
      const timeStart = new Date().toISOString();
      const response = await sessionsV2Api.bulkCreateSessions(siteId, {
        siteExecutorIds: userIds,
        timeStart
      });

      if (response.successfullyCreated > 0) {
        showSuccess(
          `Успешно открыто смен: ${response.successfullyCreated}${response.skipped > 0 ? `. Пропущено: ${response.skipped}` : ''}`
        );
        await loadActiveShifts();
        refreshCounters?.();
      } else {
        showWarning('Не удалось открыть смены. Возможно, у всех выбранных сотрудников уже есть активные смены.');
      }
      
      setShowBulkOpenSheet(false);
    } catch (error: any) {
      console.error('Ошибка при массовом открытии смен:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Ошибка при открытии смен';
      showError(`Ошибка: ${errorMessage}`);
    } finally {
      skipSignalRAddRef.current = false;
      setBulkCreating(false);
    }
  }, [siteId, loadActiveShifts, refreshCounters, showSuccess, showError]);

  const handleOpenBulkSheet = () => {
    setShowBulkOpenSheet(true);
  };

  // Эффект для показа меню при изменении выделенных смен
  useEffect(() => {
    if (selectedShiftIds.size > 0) {
      setShowActionsMenuBar(true);
    } else {
      setShowActionsMenuBar(false);
    }
  }, [selectedShiftIds]);

  // Массовое добавление контрольных точек
  const handleBulkAddCheckpoints = useCallback(async () => {
    if (selectedShiftIds.size === 0) return;

    try {
      setBulkAddingCheckpoints(true);
      const sessionIdsArray = Array.from(selectedShiftIds);
      const now = new Date();
      
      let successCount = 0;
      let errorCount = 0;
      
      for (const sessionId of sessionIdsArray) {
        try {
          // Получаем активные активности
          const existingActivities = await sessionsV2Api.getSessionActivities(siteId, sessionId);
          const activeActivities = existingActivities.filter(a => !a.timeEnd);
          
          // Закрываем все активные активности
          for (const currentActivity of activeActivities) {
            const updateData: UpdateSessionActivityRequest = {
              typeOfWorkId: currentActivity.typeOfWorkId,
              timeStart: currentActivity.timeStart,
              timeEnd: now.toISOString(),
              rate: currentActivity.rate || 0,
              contractId: currentActivity.contractId,
              comment: currentActivity.comment || ''
            };
            await sessionsV2Api.updateSessionActivity(siteId, sessionId, currentActivity.id, updateData);
          }
          
          // Создаем новую активную активность
          const newActivityData: CreateSessionActivityRequest = {
            typeOfWorkId: 1, // "Не указано" по умолчанию
            timeStart: now.toISOString(),
            timeEnd: undefined,
            rate: 0,
            contractId: undefined,
            comment: ''
          };
          await sessionsV2Api.createSessionActivity(siteId, sessionId, newActivityData);
          successCount++;
        } catch (error) {
          console.error(`Ошибка при добавлении контрольной точки в смену ${sessionId}:`, error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        showSuccess(`Контрольные точки добавлены: ${successCount}${errorCount > 0 ? `. Ошибок: ${errorCount}` : ''}`);
        await loadActiveShifts();
        refreshCounters?.();
      } else {
        showError('Не удалось добавить контрольные точки');
      }
      
      setSelectedShiftIds(new Set());
      setShowActionsMenuBar(false);
    } catch (error: any) {
      console.error('Ошибка при массовом добавлении контрольных точек:', error);
      showError('Ошибка при добавлении контрольных точек');
    } finally {
      setBulkAddingCheckpoints(false);
    }
  }, [selectedShiftIds, siteId, loadActiveShifts, refreshCounters, showSuccess, showError]);

  // Проверка пересечений активностей для массового добавления
  const checkBulkActivityOverlaps = useCallback(async (sessionIds: number[], activityStartTime: Date, activityEndTime: Date): Promise<Map<number, any[]>> => {
    const overlaps = new Map<number, any[]>();
    
    for (const sessionId of sessionIds) {
      try {
        const existingActivities = await sessionsV2Api.getSessionActivities(siteId, sessionId);
        const overlapping = existingActivities.filter(activity => {
          if (!activity.timeStart) return false;
          const activityStart = new Date(activity.timeStart);
          const activityEnd = activity.timeEnd ? new Date(activity.timeEnd) : new Date();
          return (activityStart.getTime() < activityEndTime.getTime() && 
                  activityEnd.getTime() > activityStartTime.getTime());
        });
        
        if (overlapping.length > 0) {
          overlaps.set(sessionId, overlapping);
        }
      } catch (error) {
        console.error(`Ошибка при проверке пересечений для смены ${sessionId}:`, error);
      }
    }
    
    return overlaps;
  }, [siteId]);

  // Массовое добавление активностей
  const handleBulkAddActivity = useCallback(async () => {
    if (selectedShiftIds.size === 0) return;
    
    if (activityHours === 0 && activityMinutes === 0) {
      showError('Укажите длительность активности');
      return;
    }
    
    const now = new Date();
    const durationMs = (activityHours * 60 + activityMinutes) * 60 * 1000;
    const activityStartTime = new Date(now.getTime() - durationMs);
    
    const sessionIdsArray = Array.from(selectedShiftIds);
    
    // Проверяем пересечения для всех смен
    const overlaps = await checkBulkActivityOverlaps(sessionIdsArray, activityStartTime, now);
    
    if (overlaps.size > 0) {
      // Есть пересечения - показываем предупреждение
      setBulkActivityOverlaps(overlaps);
      setPendingBulkActivityData({ startTime: activityStartTime, endTime: now });
      setShowAddActivitySheet(false);
      setShowBulkConfirmDeleteSheet(true);
      return;
    }
    
    // Нет пересечений - добавляем активности
    await performBulkAddActivity(sessionIdsArray, activityStartTime, now);
  }, [selectedShiftIds, activityHours, activityMinutes, checkBulkActivityOverlaps, showError]);

  // Выполнение массового добавления активностей
  const performBulkAddActivity = useCallback(async (sessionIds: number[], activityStartTime: Date, activityEndTime: Date) => {
    try {
      setBulkAddingActivities(true);
      
      let successCount = 0;
      let errorCount = 0;
      
      for (const sessionId of sessionIds) {
        try {
          const existingActivities = await sessionsV2Api.getSessionActivities(siteId, sessionId);
          const activeActivities = existingActivities.filter(a => !a.timeEnd);
          
          // Закрываем все активные активности
          for (const currentActivity of activeActivities) {
            const updateData: UpdateSessionActivityRequest = {
              typeOfWorkId: currentActivity.typeOfWorkId,
              timeStart: currentActivity.timeStart,
              timeEnd: activityStartTime.toISOString(),
              rate: currentActivity.rate || 0,
              contractId: currentActivity.contractId,
              comment: currentActivity.comment || ''
            };
            await sessionsV2Api.updateSessionActivity(siteId, sessionId, currentActivity.id, updateData);
          }
          
          // Удаляем пересекающиеся активности (если были)
          const overlaps = bulkActivityOverlaps.get(sessionId) || [];
          for (const activityToDelete of overlaps) {
            try {
              await sessionsV2Api.deleteSessionActivity(siteId, sessionId, activityToDelete.id);
            } catch (error) {
              console.warn(`Failed to delete activity ${activityToDelete.id}:`, error);
            }
          }
          
          // Создаем новую активность в прошлом
          const pastActivityData: CreateSessionActivityRequest = {
            typeOfWorkId: 1,
            timeStart: activityStartTime.toISOString(),
            timeEnd: activityEndTime.toISOString(),
            rate: 0,
            contractId: undefined,
            comment: ''
          };
          await sessionsV2Api.createSessionActivity(siteId, sessionId, pastActivityData);
          
          // Создаем новую активную активность
          const newActivityData: CreateSessionActivityRequest = {
            typeOfWorkId: 1,
            timeStart: activityEndTime.toISOString(),
            timeEnd: undefined,
            rate: 0,
            contractId: undefined,
            comment: ''
          };
          await sessionsV2Api.createSessionActivity(siteId, sessionId, newActivityData);
          
          successCount++;
        } catch (error) {
          console.error(`Ошибка при добавлении активности в смену ${sessionId}:`, error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        showSuccess(`Активности добавлены: ${successCount}${errorCount > 0 ? `. Ошибок: ${errorCount}` : ''}`);
        setShowAddActivitySheet(false);
        setShowBulkConfirmDeleteSheet(false);
        setActivityHours(1);
        setActivityMinutes(0);
        setBulkActivityOverlaps(new Map());
        setPendingBulkActivityData(null);
        setSelectedShiftIds(new Set());
        setShowActionsMenuBar(false);
        
        await loadActiveShifts();
        refreshCounters?.();
      } else {
        showError('Не удалось добавить активности');
      }
    } catch (error: any) {
      console.error('Ошибка при массовом добавлении активностей:', error);
      showError('Ошибка при добавлении активностей');
    } finally {
      setBulkAddingActivities(false);
    }
  }, [siteId, bulkActivityOverlaps, loadActiveShifts, refreshCounters, showSuccess, showError]);

  // Обработчик подтверждения массового удаления пересекающихся активностей
  const handleBulkConfirmDelete = useCallback(async () => {
    if (!pendingBulkActivityData) return;
    const sessionIdsArray = Array.from(selectedShiftIds);
    await performBulkAddActivity(sessionIdsArray, pendingBulkActivityData.startTime, pendingBulkActivityData.endTime);
  }, [pendingBulkActivityData, selectedShiftIds, performBulkAddActivity]);

  // Обработчик закрытия смен
  const handleCloseShifts = useCallback(async () => {
    if (selectedShiftIds.size === 0) return;

    try {
      setClosingShifts(true);
      skipSignalRRemoveRef.current = true;
      const timeEnd = new Date().toISOString();
      const sessionIdsArray = Array.from(selectedShiftIds);
      
      // Закрываем все выделенные смены одним запросом
      const response = await sessionsV2Api.bulkCloseSessions(siteId, {
        sessionIds: sessionIdsArray,
        timeEnd
      });

      // Показываем детальную информацию о результатах
      if (response.successfullyClosed === response.totalRequested) {
        // Все смены закрыты успешно
        if (response.successfullyClosed === 1) {
          showSuccess('Смена успешно закрыта');
        } else {
          showSuccess(`Успешно закрыто смен: ${response.successfullyClosed}`);
        }
      } else if (response.successfullyClosed > 0) {
        // Частично закрыто
        showWarning(
          `Закрыто смен: ${response.successfullyClosed} из ${response.totalRequested}. ` +
          `${response.skipped} смен не удалось закрыть.`
        );
      } else {
        // Ничего не закрыто
        showError('Не удалось закрыть ни одну смену');
      }
      
      // Обновляем список активных смен
      await loadActiveShifts();
      refreshCounters?.();
      
      // Сбрасываем выделение
      setSelectedShiftIds(new Set());
      setShowActionsMenuBar(false);
    } catch (error: any) {
      console.error('Ошибка при закрытии смен:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Ошибка при закрытии смен';
      showError(`Ошибка: ${errorMessage}`);
    } finally {
      skipSignalRRemoveRef.current = false;
      setClosingShifts(false);
    }
  }, [selectedShiftIds, siteId, loadActiveShifts, refreshCounters, showSuccess, showError, showWarning]);

  const handleCloseActionsMenuBar = useCallback(() => {
    setShowActionsMenuBar(false);
    // Сбрасываем выделение при закрытии меню
    setSelectedShiftIds(new Set());
  }, []);

  // Обработчик отмены выделения (для кнопки крестика)
  const handleCancelSelection = useCallback(() => {
    setSelectedShiftIds(new Set());
  }, []);

  // Подготовка действий для меню
  const shiftActions: ActionItem[] = React.useMemo(() => {
    if (selectedShiftIds.size === 0) return [];

    const isSingle = selectedShiftIds.size === 1;
    const count = selectedShiftIds.size;

    const actions: ActionItem[] = [
      {
        id: 'addCheckpoint',
        label: isSingle ? 'Добавить контрольную точку' : `Добавить контрольные точки (${count})`,
        icon: 'ri-add-circle-line',
        onClick: handleBulkAddCheckpoints,
        disabled: bulkAddingCheckpoints,
        variant: 'primary'
      },
      {
        id: 'addActivity',
        label: isSingle ? 'Добавить активность' : `Добавить активность (${count})`,
        icon: 'ri-time-line',
        onClick: () => setShowAddActivitySheet(true),
        disabled: bulkAddingActivities,
        variant: 'secondary'
      },
      {
        id: 'close',
        label: isSingle ? 'Закрыть смену' : `Закрыть смены (${count})`,
        icon: 'ri-close-circle-line',
        onClick: handleCloseShifts,
        disabled: closingShifts,
        variant: 'danger'
      }
    ];

    return actions;
  }, [selectedShiftIds, bulkAddingCheckpoints, bulkAddingActivities, closingShifts, handleBulkAddCheckpoints, handleCloseShifts]);

  // Обработчик добавления активности с длительностью (для массового или одиночного)
  const handleAddActivityWithDuration = async () => {
    if (selectedShiftIds.size > 0) {
      // Массовое добавление
      await handleBulkAddActivity();
    } else if (selectedSession?.id) {
      // Одиночное добавление (для раскрытой смены)
      if (activityHours === 0 && activityMinutes === 0) {
        showError('Укажите длительность активности');
        return;
      }
      
      const now = new Date();
      const durationMs = (activityHours * 60 + activityMinutes) * 60 * 1000;
      const activityStartTime = new Date(now.getTime() - durationMs);
      const sessionStartTime = new Date(selectedSession.timeStart);
      
      if (activityStartTime.getTime() < sessionStartTime.getTime()) {
        showError('Время начала активности не может быть раньше времени начала смены');
        return;
      }
      
      const existingActivities = await sessionsV2Api.getSessionActivities(siteId, selectedSession.id);
      const overlappingActivities = existingActivities.filter(activity => {
        if (!activity.timeStart) return false;
        const activityStart = new Date(activity.timeStart);
        const activityEnd = activity.timeEnd ? new Date(activity.timeEnd) : now;
        return activityStart.getTime() >= activityStartTime.getTime() && 
               activityStart.getTime() < now.getTime();
      });
      
      if (overlappingActivities.length > 0) {
        setActivitiesToDelete(overlappingActivities);
        setPendingActivityData({ startTime: activityStartTime, endTime: now });
        setShowAddActivitySheet(false);
        setShowConfirmDeleteSheet(true);
        return;
      }
      
      await performAddActivity(activityStartTime, now);
    }
  };

  // Выполнение добавления активности
  const performAddActivity = async (activityStartTime: Date, now: Date) => {
    if (!selectedSession?.id) return;
    
    setIsAddingActivity(true);
    try {
      const existingActivities = await sessionsV2Api.getSessionActivities(siteId, selectedSession.id);
      const activeActivities = existingActivities.filter(a => !a.timeEnd);
      
      // Закрываем все активные активности
      for (const currentActivity of activeActivities) {
        const updateData: UpdateSessionActivityRequest = {
          typeOfWorkId: currentActivity.typeOfWorkId,
          timeStart: currentActivity.timeStart,
          timeEnd: activityStartTime.toISOString(),
          rate: currentActivity.rate || 0,
          contractId: currentActivity.contractId,
          comment: currentActivity.comment || ''
        };
        await sessionsV2Api.updateSessionActivity(siteId, selectedSession.id, currentActivity.id, updateData);
      }
      
      // Удаляем пересекающиеся активности
      for (const activityToDelete of activitiesToDelete) {
        try {
          await sessionsV2Api.deleteSessionActivity(siteId, selectedSession.id, activityToDelete.id);
        } catch (error) {
          console.warn(`Failed to delete activity ${activityToDelete.id}:`, error);
        }
      }
      
      // Создаем новую активность в прошлом
      const pastActivityData: CreateSessionActivityRequest = {
        typeOfWorkId: 1,
        timeStart: activityStartTime.toISOString(),
        timeEnd: now.toISOString(),
        rate: 0,
        contractId: undefined,
        comment: ''
      };
      await sessionsV2Api.createSessionActivity(siteId, selectedSession.id, pastActivityData);
      
      // Создаем новую активную активность
      const newActivityData: CreateSessionActivityRequest = {
        typeOfWorkId: 1,
        timeStart: now.toISOString(),
        timeEnd: undefined,
        rate: 0,
        contractId: undefined,
        comment: ''
      };
      await sessionsV2Api.createSessionActivity(siteId, selectedSession.id, newActivityData);
      
      showSuccess('Активность добавлена');
      setShowAddActivitySheet(false);
      setShowConfirmDeleteSheet(false);
      setActivityHours(1);
      setActivityMinutes(0);
      setActivitiesToDelete([]);
      setPendingActivityData(null);
      
      // Перезагружаем активности
      const activities = await sessionsV2Api.getSessionActivities(siteId, selectedSession.id);
      setSelectedSessionActivities(activities);
      await loadActiveShifts();
    } catch (error: any) {
      console.error('Ошибка при добавлении активности:', error);
      let errorMessage = 'Ошибка при добавлении активности. Попробуйте еще раз.';
      if (error?.response) {
        const responseData = error.response.data;
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showError(errorMessage);
    } finally {
      setIsAddingActivity(false);
    }
  };

  // Обработчик подтверждения удаления пересекающихся активностей
  const handleConfirmDelete = async () => {
    if (!pendingActivityData) return;
    await performAddActivity(pendingActivityData.startTime, pendingActivityData.endTime);
  };

  const previewExecutors = useMemo(() => {
    const byUser = new Map<number, { userId: number; name: string; avatarUrl?: string }>();
    activeShifts.forEach((shift) => {
      if (!byUser.has(shift.userId)) {
        const avatarUrl =
          (shift.siteExecutorId != null && avatarByExecutorId.get(shift.siteExecutorId)) ||
          avatarByUserId.get(shift.userId);
        byUser.set(shift.userId, {
          userId: shift.userId,
          name: shift.user?.name || '—',
          avatarUrl,
        });
      }
    });
    return Array.from(byUser.values());
  }, [activeShifts, avatarByUserId, avatarByExecutorId]);

  const previewPeopleCount = previewExecutors.length;

  const renderCollapsedPreview = () => (
    <div className={previewStyles.collapsedPreview}>
      <span className={previewStyles.collapsedLabel}>
        {isLoading
          ? 'Сейчас на объекте…'
          : `Сейчас на объекте ${previewPeopleCount} ${getPeopleWordForm(previewPeopleCount)}`}
      </span>
      <div className={previewStyles.avatarRow}>
        <div className={previewStyles.avatarScroll}>
          {isLoading ? (
            <>
              <span className={previewStyles.avatarSkeleton} aria-hidden />
              <span className={previewStyles.avatarSkeleton} aria-hidden />
              <span className={previewStyles.avatarSkeleton} aria-hidden />
            </>
          ) : previewExecutors.length === 0 ? (
            <span className={previewStyles.emptyHint}>На объекте нет активных смен</span>
          ) : (
            previewExecutors.map((executor) => (
              <PreviewExecutorAvatar
                key={executor.userId}
                userId={executor.userId}
                name={executor.name}
                avatarUrl={executor.avatarUrl}
              />
            ))
          )}
        </div>
        {canCreateSessions && (
          <button
            type="button"
            className={previewStyles.addButton}
            aria-label="Массовое открытие смен"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenBulkSheet();
            }}
          >
            <i className="ri-add-line" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
    <DashboardCard
      id="active-shifts-v2-card"
      title="Активные смены"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      counters={[counter ?? 0]}
      countersLoading={countersLoading}
      warningCounter={unsetActivitiesCount != null && unsetActivitiesCount > 0 ? unsetActivitiesCount : undefined}
      expandMode="fullscreen"
      isFullscreenOpen={isFullscreenOpen}
      onFullscreenOpenChange={setIsFullscreenOpen}
      collapsedPreview={renderCollapsedPreview()}
      collapsedPreviewVariant="active-shifts"
    >
      {/* Кнопка массового открытия смен (только если есть права) */}
      {canCreateSessions && (
        <div className={styles.bulkOpenButtonContainer}>
          <StatusActionButton
            variant="primary"
            onClick={handleOpenBulkSheet}
            icon="ri-user-add-line"
            size="small"
            fullWidth
          >
            Массовое открытие смен
          </StatusActionButton>
        </div>
      )}

      {/* Статистика */}
      <StatsGrid items={statsData} />

      {/* Кнопка выделения всех смен */}
      {!isLoading && activeShifts.length > 0 && (
        <div className={styles.selectAllSection}>
          <StatusActionButton
            variant="primary"
            fullWidth
            onClick={handleSelectAll}
            disabled={isLoading}
            icon={
              activeShifts.every(s => selectedShiftIds.has(s.id))
                ? 'ri-checkbox-multiple-line'
                : 'ri-checkbox-blank-line'
            }
            title={
              activeShifts.every(s => selectedShiftIds.has(s.id))
                ? 'Снять выделение со всех смен'
                : 'Выделить все смены'
            }
          >
            {activeShifts.every(s => selectedShiftIds.has(s.id))
              ? 'Снять выделение'
              : 'Выделить все'}
            {selectedShiftIds.size > 0 && (
              <span className={styles.selectedCount}>
                {' '}({selectedShiftIds.size} из {activeShifts.length})
              </span>
            )}
          </StatusActionButton>
        </div>
      )}

      {/* Список активных смен по объектам */}
      <DataList
        items={dataListItems}
        loading={isLoading}
        loadingText="Загрузка активных смен..."
        emptyState={{
          icon: 'ri-user-unfollow-line',
          title: 'Нет активных смен',
          subtitle: 'Сейчас никто не работает'
        }}
        variant="detailed"
        showDividers={true}
        headerStyle="minimal"
      />

      {/* Нижнее меню действий */}
      <ActionsMenuBar
        isOpen={showActionsMenuBar}
        actions={shiftActions}
        onClose={handleCloseActionsMenuBar}
        onCancel={handleCancelSelection}
      />

      {/* Bottom Sheet для заполнения активностей */}
      <BottomSheet
        isOpen={showActivitiesSheet}
        onClose={handleCloseActivitiesSheet}
        title="Указать виды работ"
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        {sessionForActivities && (() => {
          const sessionStart = new Date(sessionForActivities.timeStart);
          const sessionEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? new Date(sessionForActivities.timeEnd) : new Date());
          
          const formatDurationDisplay = (hours: number): string => {
            const h = Math.floor(hours);
            const m = Math.round((hours - h) * 60);
            if (m === 0) {
              return `${h}ч`;
            }
            return `${h}ч ${m}м`;
          };

          return (
            <div>
              <div className="activities-section-wrapper">
                <div className="activities-section-header">
                  <div className="activities-section-title-container">
                    <div className="section-title">
                      <h3>Активности</h3>
                    </div>
                  </div>
                  <div className="activities-section-divider"></div>
                </div>
                <div className="activities-section-actions">
                  <button
                    type="button"
                    className="btn-add-row"
                    onClick={handleAddActivity}
                  >
                    <i className="ri-add-line"></i>
                    <span>Добавить активности</span>
                  </button>
                </div>
                <div className="activities-section">
                  {activities.length >= 1 && (() => {
                    const timelineSegmentsData: TimelineSegment[] = timelineSegments.map((segment) => {
                      const workTypeName = segment.activity.typeOfWorkName || 
                        typesOfWork.find(t => t.id === segment.activity.typeOfWorkId)?.name || 
                        'Не выбрано';
                      
                      return {
                        id: segment.activity.id,
                        start: segment.start,
                        end: segment.end,
                        label: workTypeName,
                        duration: segment.duration !== undefined 
                          ? segment.duration 
                          : segment.activity.hours,
                        data: segment.activity
                      };
                    });

                    const timelineHandles: TimelineHandle[] = intersectionTimes.map((time, index) => ({
                      id: `handle-${index}`,
                      time
                    }));

                    return (
                      <Timeline
                        startTime={sessionStart}
                        endTime={sessionEnd}
                        segments={timelineSegmentsData}
                        handles={timelineHandles}
                        onHandleDrag={(handleId, newTime) => {
                          const handleIndex = parseInt(handleId.toString().replace('handle-', ''));
                          if (!isNaN(handleIndex) && sortedActivities.length > handleIndex + 1) {
                            const nextActivity = sortedActivities[handleIndex + 1];
                            setActivities(prev => {
                              const index = prev.findIndex(a => a.id === nextActivity.id);
                              if (index === -1) return prev;
                              
                              const updated = [...prev];
                              updated[index] = {
                                ...updated[index],
                                timeStart: newTime,
                                isModified: true
                              };
                              const currentStart = new Date(sessionForActivities.timeStart);
                              const currentEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? new Date(sessionForActivities.timeEnd) : new Date());
                              return recalculateStats(updated, currentStart, currentEnd);
                            });
                          }
                        }}
                        onHandleDragEnd={(handleId, newTime) => {
                          // Изменения уже применены в onHandleDrag
                        }}
                        formatTime={formatTimeCallback}
                        formatDuration={formatDurationDisplay}
                        minSegmentWidth={8}
                        showTimeLabels={true}
                        showDuration={true}
                        showLabels={true}
                        timelineRef={setTimelineRef}
                        className="activities-timeline"
                      />
                    );
                  })()}

                  <div className="activities-table">
                    <div className="table-body">
                      {activities.length > 0 && (
                        <>
                          <div className="activity-separator">
                            <div className="activity-separator-line"></div>
                            <div className="activity-separator-time">
                              <span title="Начало смены" className="activity-start-time-label">
                                {formatTimeCallback(activities[0].timeStart)}
                              </span>
                            </div>
                          </div>
                          {activities.map((item, index) => (
                            <ActivityItemComponent
                              key={item.id}
                              item={item}
                              index={index}
                              activities={activities}
                              sessionEnd={sessionEnd}
                              formatTime={formatTimeCallback}
                              formatDuration={formatDurationAsObject}
                              getActivityEndTime={getActivityEndTime}
                              updateActivity={updateActivity}
                              handleDeleteActivity={handleDeleteActivity}
                              onCommentClick={(item) => {
                                setCurrentActivityForComment(item);
                                setActivityComment(item.comment || '');
                                setShowCommentSheet(true);
                              }}
                              onActivityTypeClick={handleOpenActivityTypeEditor}
                              isLast={index === activities.length - 1}
                            />
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="create-session-form-content">
                <div className="form-actions">
                  <StatusActionButton
                    type="button"
                    variant="secondary"
                    onClick={handleCloseActivitiesSheet}
                    disabled={isEnding}
                  >
                    Отмена
                  </StatusActionButton>
                  <StatusActionButton
                    type="button"
                    variant="primary"
                    onClick={handleSaveActivities}
                    loading={isEnding}
                    disabled={activities.length === 0}
                    icon="ri-save-line"
                  >
                    Сохранить активности
                  </StatusActionButton>
                </div>
              </div>
            </div>
          );
        })()}
      </BottomSheet>

      {/* Bottom sheet для добавления активности с длительностью */}
      <BottomSheet
        isOpen={showAddActivitySheet}
        onClose={() => {
          setShowAddActivitySheet(false);
          setActivityHours(1);
          setActivityMinutes(0);
        }}
        title={selectedShiftIds.size > 0 ? `Добавить активность (${selectedShiftIds.size} смен)` : "Добавить активность"}
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#e0e0e0'
            }}>
              Длительность активности
            </label>
            
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              alignItems: 'center' 
            }}>
              <div style={{ flex: 1 }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '12px', 
                  color: '#888'
                }}>
                  Часы
                </label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={activityHours}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setActivityHours(Math.max(0, Math.min(23, value)));
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    color: '#e0e0e0',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
              
              <div style={{ flex: 1 }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '12px', 
                  color: '#888'
                }}>
                  Минуты
                </label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={activityMinutes}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setActivityMinutes(Math.max(0, Math.min(59, value)));
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    color: '#e0e0e0',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            
            <div style={{ 
              marginTop: '12px', 
              padding: '12px', 
              backgroundColor: '#1a1a1a', 
              borderRadius: '8px',
              fontSize: '13px',
              color: '#888'
            }}>
              {selectedShiftIds.size > 0 
                ? `Активность будет добавлена в прошлое на указанный период времени во всех выделенных сменах (${selectedShiftIds.size})`
                : 'Активность будет добавлена в прошлое на указанный период времени'
              }
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px' 
          }}>
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAddActivitySheet(false);
                setActivityHours(1);
                setActivityMinutes(0);
              }}
              disabled={isAddingActivity}
              fullWidth
            >
              Отмена
            </StatusActionButton>
            
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={handleAddActivityWithDuration}
              loading={isAddingActivity}
              disabled={isAddingActivity || (activityHours === 0 && activityMinutes === 0)}
              fullWidth
              icon="ri-add-line"
            >
              Добавить
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Модальное окно подтверждения удаления пересекающихся активностей (одиночное) */}
      <BottomSheet
        isOpen={showConfirmDeleteSheet}
        onClose={() => {
          setShowConfirmDeleteSheet(false);
          setActivitiesToDelete([]);
          setPendingActivityData(null);
          setShowAddActivitySheet(true);
        }}
        title="Подтверждение удаления"
        size="medium"
        showHandle={true}
        allowSwipeToClose={false}
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#2a1a0a',
              border: '1px solid #ff8800',
              borderRadius: '8px'
            }}>
              <i className="ri-alert-line" style={{ fontSize: '24px', color: '#ff8800' }}></i>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#ff8800', marginBottom: '4px' }}>
                  Внимание!
                </div>
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  Добавляемая активность пересекается с существующими активностями
                </div>
              </div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', color: '#888', marginBottom: '8px' }}>
                Будет удалено активностей: <strong style={{ color: '#e0e0e0' }}>{activitiesToDelete.length}</strong>
              </div>
              
              {activitiesToDelete.length > 0 && (
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  backgroundColor: '#1a1a1a',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  {activitiesToDelete.map((activity, index) => {
                    const hours = activity.hours ?? activity.allocatedHours ?? 0;
                    const timeDisplay = activity.timeStart
                      ? (activity.timeEnd
                          ? `${new Date(activity.timeStart).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} - ${new Date(activity.timeEnd).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
                          : `${new Date(activity.timeStart).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} - текущее время`)
                      : null;
                    
                    return (
                      <div key={activity.id} style={{ 
                        padding: '8px 0',
                        borderBottom: index < activitiesToDelete.length - 1 ? '1px solid #333' : 'none'
                      }}>
                        <div style={{ fontSize: '13px', color: '#e0e0e0' }}>
                          {activity.typeOfWorkName || 'Не указано'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                          {timeDisplay ? `${timeDisplay} (${hours.toFixed(1)} ч)` : `${hours.toFixed(1)} ч`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#1a1a1a', 
              borderRadius: '8px',
              fontSize: '13px',
              color: '#888'
            }}>
              Вы уверены, что хотите удалить эти активности? Добавленная активность заменит их.
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px' 
          }}>
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowConfirmDeleteSheet(false);
                setActivitiesToDelete([]);
                setPendingActivityData(null);
                setShowAddActivitySheet(true);
              }}
              disabled={isAddingActivity}
              fullWidth
            >
              Отмена
            </StatusActionButton>
            
            <StatusActionButton
              type="button"
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isAddingActivity}
              disabled={isAddingActivity}
              fullWidth
              icon="ri-delete-bin-line"
            >
              Удалить и добавить
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Модальное окно подтверждения массового удаления пересекающихся активностей */}
      <BottomSheet
        isOpen={showBulkConfirmDeleteSheet}
        onClose={() => {
          setShowBulkConfirmDeleteSheet(false);
          setBulkActivityOverlaps(new Map());
          setPendingBulkActivityData(null);
          setShowAddActivitySheet(true);
        }}
        title="Подтверждение массового удаления"
        size="large"
        showHandle={true}
        allowSwipeToClose={false}
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#2a1a0a',
              border: '1px solid #ff8800',
              borderRadius: '8px'
            }}>
              <i className="ri-alert-line" style={{ fontSize: '24px', color: '#ff8800' }}></i>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#ff8800', marginBottom: '4px' }}>
                  Внимание!
                </div>
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  Добавляемая активность пересекается с существующими активностями в некоторых сменах
                </div>
              </div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', color: '#888', marginBottom: '8px' }}>
                Смен с пересечениями: <strong style={{ color: '#e0e0e0' }}>{bulkActivityOverlaps.size}</strong>
              </div>
              
              {bulkActivityOverlaps.size > 0 && (
                <div style={{ 
                  maxHeight: '300px', 
                  overflowY: 'auto',
                  backgroundColor: '#1a1a1a',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  {Array.from(bulkActivityOverlaps.entries()).map(([sessionId, activities], sessionIndex) => {
                    const shift = activeShifts.find(s => s.id === sessionId);
                    const userName = shift?.user?.name || 'Неизвестный сотрудник';
                    
                    return (
                      <div key={sessionId} style={{ 
                        marginBottom: sessionIndex < bulkActivityOverlaps.size - 1 ? '16px' : '0',
                        paddingBottom: sessionIndex < bulkActivityOverlaps.size - 1 ? '16px' : '0',
                        borderBottom: sessionIndex < bulkActivityOverlaps.size - 1 ? '1px solid #333' : 'none'
                      }}>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: '600',
                          color: '#e0e0e0',
                          marginBottom: '8px'
                        }}>
                          {userName} ({activities.length} активностей)
                        </div>
                        {activities.map((activity, index) => {
                          const hours = activity.hours ?? activity.allocatedHours ?? 0;
                          const timeDisplay = activity.timeStart
                            ? (activity.timeEnd
                                ? `${new Date(activity.timeStart).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} - ${new Date(activity.timeEnd).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
                                : `${new Date(activity.timeStart).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} - текущее время`)
                            : null;
                          
                          return (
                            <div key={activity.id} style={{ 
                              padding: '6px 0',
                              fontSize: '12px',
                              color: '#888'
                            }}>
                              {activity.typeOfWorkName || 'Не указано'} - {timeDisplay ? `${timeDisplay} (${hours.toFixed(1)} ч)` : `${hours.toFixed(1)} ч`}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#1a1a1a', 
              borderRadius: '8px',
              fontSize: '13px',
              color: '#888'
            }}>
              Вы уверены, что хотите удалить эти активности? Добавленная активность заменит их во всех выделенных сменах.
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px' 
          }}>
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowBulkConfirmDeleteSheet(false);
                setBulkActivityOverlaps(new Map());
                setPendingBulkActivityData(null);
                setShowAddActivitySheet(true);
              }}
              disabled={bulkAddingActivities}
              fullWidth
            >
              Отмена
            </StatusActionButton>
            
            <StatusActionButton
              type="button"
              variant="danger"
              onClick={handleBulkConfirmDelete}
              loading={bulkAddingActivities}
              disabled={bulkAddingActivities}
              fullWidth
              icon="ri-delete-bin-line"
            >
              Удалить и добавить
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Модальное окно выбора видов работ / контрактов */}
      <BottomSheet
        isOpen={showWorkTypeSelector}
        onClose={handleCloseWorkTypeSelector}
        title={editingActivityId ? "Изменить тип активности" : "Выберите виды работ или контракты"}
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        <div className="work-type-selector-content">
          <div className="activity-source-tabs-wrapper">
            <HorizontalCategoryTabs
              categories={['Ставка', 'Сделка']}
              value={activitySourceTab === 'ставка' ? 'Ставка' : 'Сделка'}
              onChange={(v) => setActivitySourceTab(v === 'Сделка' ? 'сделка' : 'ставка')}
              size="medium"
              className="activity-source-tabs"
            />
          </div>

          {activitySourceTab === 'ставка' && (
            <>
              <div className="work-type-search">
                <div className="search-input-wrapper">
                  <i className="ri-search-line search-icon"></i>
                  <input
                    type="text"
                    placeholder="Поиск по названию или категории..."
                    value={workTypeSearchQuery}
                    onChange={(e) => setWorkTypeSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>

              {(() => {
                const categories = Array.from(new Set(typesOfWork.map(type => type.section)));
                return categories.length > 0 && (
                  <HorizontalCategoryTabs
                    categories={categories}
                    value={selectedWorkTypeCategory}
                    onChange={setSelectedWorkTypeCategory}
                    showAllOption={true}
                    allOptionLabel="Все категории"
                    size="medium"
                  />
                );
              })()}

              <div className="work-type-list">
                {loadingWorkTypes ? (
                  <div className="work-type-loading">
                    <i className="ri-loader-4-line ri-spin"></i>
                    <span>Загрузка типов работ...</span>
                  </div>
                ) : Object.keys(groupedTypes).length === 0 ? (
                  <div className="work-type-empty">
                    <i className="ri-search-line"></i>
                    <span>Типы работ не найдены</span>
                  </div>
                ) : (
                  Object.entries(groupedTypes).map(([category, types]) => (
                    <div key={category} className="work-type-category-group">
                      <div className="category-header">
                        <h4>{category}</h4>
                        <span className="category-count">{types.length}</span>
                      </div>
                      <div className="work-type-grid">
                        {types.map(type => {
                          const isSelected = selectedWorkTypeIds.includes(type.id);
                          return (
                            <div
                              key={type.id}
                              className={`work-type-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleWorkTypeSelection(type.id)}
                            >
                              <div className="work-type-card-content">
                                <div className="work-type-card-name">{type.name}</div>
                              </div>
                              {isSelected && (
                                <div className="work-type-card-check">
                                  <i className="ri-check-line"></i>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {activitySourceTab === 'сделка' && (
            <>
              <div className="work-type-search">
                <div className="search-input-wrapper">
                  <i className="ri-search-line search-icon"></i>
                  <input
                    type="text"
                    placeholder="Поиск по контрактам..."
                    value={contractSearchQuery}
                    onChange={(e) => setContractSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>
              <div className="work-type-list">
                {loadingContracts ? (
                  <div className="work-type-loading">
                    <i className="ri-loader-4-line ri-spin"></i>
                    <span>Загрузка контрактов...</span>
                  </div>
                ) : filteredContracts.length === 0 ? (
                  <div className="work-type-empty">
                    <i className="ri-file-list-3-line"></i>
                    <span>Контракты не найдены</span>
                  </div>
                ) : (
                  <div className="work-type-category-group">
                    <div className="work-type-grid">
                      {filteredContracts.map(contract => {
                        const isSelected = selectedContractIds.includes(contract.id);
                        return (
                          <div
                            key={contract.id}
                            className={`work-type-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleContractSelection(contract.id)}
                          >
                            <div className="work-type-card-content">
                              <div className="work-type-card-name">{contract.title}</div>
                              {contract.description && (
                                <div className="work-type-card-subtitle">{contract.description}</div>
                              )}
                            </div>
                            {isSelected && (
                              <div className="work-type-card-check">
                                <i className="ri-check-line"></i>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="work-type-actions">
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={handleCloseWorkTypeSelector}
            >
              Отмена
            </StatusActionButton>
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={handleConfirmWorkTypeSelection}
              disabled={(activitySourceTab === 'ставка' ? selectedWorkTypeIds.length : selectedContractIds.length) === 0}
            >
              {editingActivityId
                ? 'Сохранить'
                : `Добавить (${activitySourceTab === 'ставка' ? selectedWorkTypeIds.length : selectedContractIds.length})`}
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Bottom Sheet для ввода комментария к активности */}
      <BottomSheet
        isOpen={showCommentSheet}
        onClose={() => {
          setShowCommentSheet(false);
          setCurrentActivityForComment(null);
          setActivityComment('');
        }}
        title="Комментарий к активности"
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              color: '#e0e0e0', 
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Комментарий
            </label>
            <textarea
              value={activityComment}
              onChange={(e) => setActivityComment(e.target.value)}
              placeholder="Введите комментарий к активности..."
              rows={5}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#2a2a2a',
                color: '#e0e0e0',
                border: '1px solid #444',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: '100px'
              }}
            />
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px' 
          }}>
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowCommentSheet(false);
                setCurrentActivityForComment(null);
                setActivityComment('');
              }}
              fullWidth
            >
              Отмена
            </StatusActionButton>
            
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={() => {
                if (currentActivityForComment) {
                  updateActivity(currentActivityForComment.id, { comment: activityComment });
                  showSuccess('Комментарий сохранен. Не забудьте сохранить активности!');
                }
                setShowCommentSheet(false);
                setCurrentActivityForComment(null);
                setActivityComment('');
              }}
              fullWidth
            >
              Сохранить
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>
    </DashboardCard>

    {/* Вне fullscreen-карточки: иначе sheet не монтируется в свёрнутом режиме и ломается стек «Назад» */}
    <BulkOpenSessionsSheet
      isOpen={showBulkOpenSheet}
      onClose={() => setShowBulkOpenSheet(false)}
      siteId={siteId}
      onConfirm={handleBulkOpenSessions}
      loading={bulkCreating}
      elevated={isFullscreenOpen}
    />
    </>
  );
};

export default ActiveShiftsV2Card;

